import { prefersReducedMotion } from '@/utils/motion'
import { useEffect, useState } from 'react'

/** Duolingo types at roughly this rate when the line is short. */
const MS_PER_CHARACTER = 22

/**
 * Long lines are typed faster rather than for longer. A question that takes two
 * seconds to finish appearing is a question the user is waiting on, and this
 * sits at the top of every v2 screen.
 */
const MAX_DURATION_MS = 850

/**
 * Reveals text one character at a time, the way Duo speaks in Duolingo's
 * register flow. The mascot having a voice is most of what separates that flow
 * from a form with a cartoon next to it.
 *
 * The full string is always in the DOM at full size behind the visible layer,
 * so the bubble is its final height from the first frame. Without that the
 * bubble grows a line partway through typing and shoves the answers down under
 * the user's thumb.
 */
export default function DuoTypedText({
  className,
  text,
}: {
  className?: string
  text: string
}) {
  const [revealed, setRevealed] = useState(() =>
    prefersReducedMotion() ? text.length : 0,
  )

  useEffect(() => {
    if (prefersReducedMotion()) {
      setRevealed(text.length)
      return
    }

    setRevealed(0)

    const step = Math.min(MS_PER_CHARACTER, MAX_DURATION_MS / text.length)
    const interval = setInterval(() => {
      setRevealed((previous) => {
        if (previous >= text.length) {
          clearInterval(interval)
          return previous
        }

        return previous + 1
      })
    }, step)

    return () => clearInterval(interval)
  }, [text])

  return (
    <span className={className}>
      {/* The animation is decoration; assistive tech gets the line in full. */}
      <span className="sr-only">{text}</span>

      <span aria-hidden className="relative block">
        <span className="invisible">{text}</span>

        <span className="absolute inset-0">{text.slice(0, revealed)}</span>
      </span>
    </span>
  )
}
